import {
  KeyboardEvent,
  ReactNode,
  TouchEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import debounce from 'just-debounce';

import { CarouselButton } from './carousel-button';
import { getCarouselSlideItems } from './lib';
import { SLIDE_MARGIN_RIGHT } from './constants';
import styles from './styles.module.scss';
import CarouselSlides from './carousel-slides';

export type CarouselProps = {
  children: ReactNode;
  arrowsContainerId?: string;
  isCycled?: boolean;
  itemsPerSlide?: number;
  prevButtonAriaLabel?: string;
  nextButtonAriaLabel?: string;
  slideClassNames?: string;
  testId?: string;
};

export const Carousel = ({
  children,
  arrowsContainerId,
  slideClassNames,
  testId,
  isCycled = false,
  itemsPerSlide = 1,
  prevButtonAriaLabel = 'Previous Slide',
  nextButtonAriaLabel = 'Next Slide',
}: CarouselProps) => {
  const [initialTouchPosition, setInitialTouchPosition] = useState<
    number | null
  >(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [isMouseDown, setIsMouseDown] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [translateValue, setTranslateValue] = useState(0);

  const carouselRef = useRef<HTMLUListElement>(null);
  const prevCurrentIndexRef = useRef<number>(currentIndex);

  const slideItems = useMemo(() => {
    return getCarouselSlideItems(children, itemsPerSlide);
  }, [children, itemsPerSlide]);

  const slidesLength = slideItems.length;
  const slideWidth = () => carouselRef.current?.clientWidth;

  const isFirstSlideActive = currentIndex === 0;
  const isPrevButtonDisabled = !isCycled && isFirstSlideActive;

  const isLastSlideActive = currentIndex === slidesLength - 1;
  const isNextButtonDisabled = !isCycled && isLastSlideActive;

  const currentSlideNumber = currentIndex + 1;
  const activeSlideInfo = `${currentSlideNumber}/${slidesLength}`;

  const arrowsContainer = arrowsContainerId
    ? document.getElementById(arrowsContainerId)
    : null;

  const changeSlide = (n: number) => {
    const changedIndex = currentIndex + n;
    const slideSize = slideWidth();

    if (changedIndex < 0) {
      setCurrentIndex(slidesLength - 1);

      if (slideSize) {
        setTranslateValue(
          (slideSize + SLIDE_MARGIN_RIGHT) * -(slidesLength - 1),
        );
      }
      return;
    }

    if (changedIndex === slidesLength) {
      setCurrentIndex(0);
      setTranslateValue(0);
      return;
    }

    setCurrentIndex(currentIndex + n);

    if (slideSize) {
      setTranslateValue(translateValue - (slideSize + SLIDE_MARGIN_RIGHT) * n);
    }
  };

  const prevSlide = () => changeSlide(-1);
  const nextSlide = () => changeSlide(1);

  const handleCarouselKeyDown = (e: KeyboardEvent) => {
    if (!carouselRef.current) return;

    // make carousel non-focusable during default keyboard navigation
    if (e.code === 'Tab') {
      carouselRef.current.setAttribute('tabIndex', '-1');
      return;
    }

    if (e.code === 'ArrowLeft' && !isPrevButtonDisabled) {
      e.preventDefault();

      carouselRef.current.setAttribute('tabIndex', '0');
      prevSlide();

      // without focusing on carousel further slide change using keyboard won't work
      carouselRef.current.focus();
      return;
    }

    if (e.code === 'ArrowRight' && !isNextButtonDisabled) {
      e.preventDefault();

      carouselRef.current.setAttribute('tabIndex', '0');
      nextSlide();
      carouselRef.current.focus();
    }
  };

  const swipeSlide = (diff: number) => {
    if (diff > 5) {
      nextSlide();
      setIsMouseDown(false);
      setInitialTouchPosition(null);
    }

    if (diff < -5) {
      prevSlide();
      setIsMouseDown(false);
      setInitialTouchPosition(null);
    }
  };

  const handleMouseDown = (e: MouseEvent<HTMLUListElement>) => {
    setInitialTouchPosition(e.clientX);
    setIsMouseDown(true);
  };

  const handleMouseMove = (e: MouseEvent<HTMLUListElement>) => {
    if (!isMouseDown || initialTouchPosition === null) return;

    const diff = initialTouchPosition - e.clientX;
    swipeSlide(diff);
  };

  const handleMouseUp = () => {
    setIsMouseDown(false);
    setInitialTouchPosition(null);
  };

  const handleTouchStart = (e: TouchEvent<HTMLUListElement>) => {
    setTouchStartY(e.touches[0].clientY);
    setInitialTouchPosition(e.touches[0].clientX);
  };

  const handleTouchMove = (e: TouchEvent<HTMLUListElement>) => {
    if (initialTouchPosition === null) {
      return;
    }

    const diffX = initialTouchPosition - e.touches[0].clientX;
    const diffY = touchStartY !== null ? touchStartY - e.touches[0].clientY : 0;

    // Prevent vertical scrolling if horizontal swipe is prominent.
    if (Math.abs(diffY) < Math.abs(diffX)) {
      e.preventDefault();

      swipeSlide(diffX);
    }
  };

  const handleTouchEnd = () => {
    setTouchStartY(null);
    setInitialTouchPosition(null);
  };

  const recalculateSlide = useCallback(
    (entries: ResizeObserverEntry[]) => {
      const carouselElementEntry = entries[0];
      const carouselElement = carouselElementEntry.target as HTMLElement;
      const carouseElementWidth = carouselElement.clientWidth;

      const prevCurrentIndex = prevCurrentIndexRef.current;
      const hasSlideChanged = currentIndex !== prevCurrentIndex;

      if (hasSlideChanged) {
        prevCurrentIndexRef.current = currentIndex;
        return;
      }

      setTranslateValue(
        (carouseElementWidth + SLIDE_MARGIN_RIGHT) * currentIndex * -1,
      );
    },
    [currentIndex],
  );

  useEffect(() => {
    const carousel = carouselRef.current;

    if (!carousel) return;

    const resizeObserver = new ResizeObserver(debounce(recalculateSlide, 100));

    resizeObserver.observe(carousel);

    return () => {
      resizeObserver.unobserve(carousel);
    };
  }, [recalculateSlide]);

  const carouselArrows = (
    <div className={styles.carouselArrows}>
      <CarouselButton
        variant="prev"
        onClick={prevSlide}
        disabled={isPrevButtonDisabled}
        ariaLabel={prevButtonAriaLabel}
      />
      <span className={styles.slideNumber}>{activeSlideInfo}</span>
      <CarouselButton
        variant="next"
        onClick={nextSlide}
        disabled={isNextButtonDisabled}
        ariaLabel={nextButtonAriaLabel}
      />
    </div>
  );

  return (
    <>
      {arrowsContainer
        ? createPortal(carouselArrows, arrowsContainer)
        : carouselArrows}

      <div className={styles.carousel} onKeyDown={handleCarouselKeyDown}>
        <ul
          data-testid={testId}
          className={styles.track}
          ref={carouselRef}
          style={{
            transform: `translateX(${translateValue}px)`,
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          <CarouselSlides
            slideItems={slideItems}
            currentIndex={currentIndex}
            classNames={slideClassNames}
          />
        </ul>
      </div>
    </>
  );
};
